import type { ReportApi } from "../../api/ReportApi";
import type { ApClassSignupStaff } from "../../generated/dto/ApClassSignupStaff";
import type { ApClassSignupStaffFetchOptions } from "../../generated/dto/ApClassSignupStaffFetchOptions";
import type { CsvExportOptions } from "../../generated/dto/CsvExportOptions";
import type { DailySalesCategoryAmountSearchParams } from "../../generated/dto/DailySalesCategoryAmountSearchParams";
import type { DailySalesCategoryAmountStaff } from "../../generated/dto/DailySalesCategoryAmountStaff";
import type { DailySalesCategoryAmountStaffSearchResult } from "../../generated/dto/DailySalesCategoryAmountStaffSearchResult";
import type { DailySalesDataset } from "../../generated/dto/DailySalesDataset";
import type { DailySalesPaymentTypeAmountSearchParams } from "../../generated/dto/DailySalesPaymentTypeAmountSearchParams";
import type { DailySalesPaymentTypeAmountStaff } from "../../generated/dto/DailySalesPaymentTypeAmountStaff";
import type { DailySalesPaymentTypeAmountStaffSearchResult } from "../../generated/dto/DailySalesPaymentTypeAmountStaffSearchResult";
import type { DailySalesReportFetchOptions } from "../../generated/dto/DailySalesReportFetchOptions";
import type { DailySalesReportSearchParams } from "../../generated/dto/DailySalesReportSearchParams";
import type { DailySalesReportStaff } from "../../generated/dto/DailySalesReportStaff";
import type { DailySalesReportStaffSearchResult } from "../../generated/dto/DailySalesReportStaffSearchResult";
import type { DailySalesRequest } from "../../generated/dto/DailySalesRequest";
import type { DailySalesStatus } from "../../generated/dto/DailySalesStatus";
import type { InvalidOrderRangeRun } from "../../generated/dto/InvalidOrderRangeRun";
import type { InvalidOrderReasonSearchParams } from "../../generated/dto/InvalidOrderReasonSearchParams";
import type { InvalidOrderReasonStaff } from "../../generated/dto/InvalidOrderReasonStaff";
import type { InvalidOrderReasonStaffSearchResult } from "../../generated/dto/InvalidOrderReasonStaffSearchResult";
import type { InvalidOrderReportFetchOptions } from "../../generated/dto/InvalidOrderReportFetchOptions";
import type { InvalidOrderReportSearchParams } from "../../generated/dto/InvalidOrderReportSearchParams";
import type { InvalidOrderReportStaff } from "../../generated/dto/InvalidOrderReportStaff";
import type { InvalidOrderReportStaffSearchResult } from "../../generated/dto/InvalidOrderReportStaffSearchResult";
import type { InvalidOrderRequest } from "../../generated/dto/InvalidOrderRequest";
import type { InvalidOrderStatus } from "../../generated/dto/InvalidOrderStatus";
import type { InvalidSignupReasonFetchOptions } from "../../generated/dto/InvalidSignupReasonFetchOptions";
import type { InvalidSignupReasonSearchParams } from "../../generated/dto/InvalidSignupReasonSearchParams";
import type { InvalidSignupReasonStaff } from "../../generated/dto/InvalidSignupReasonStaff";
import type { InvalidSignupReasonStaffSearchResult } from "../../generated/dto/InvalidSignupReasonStaffSearchResult";
import type { InvalidSignupReconcileScope } from "../../generated/dto/InvalidSignupReconcileScope";
import type { InvalidSignupReconcileStatus } from "../../generated/dto/InvalidSignupReconcileStatus";
import type { InvalidSignupReportFetchOptions } from "../../generated/dto/InvalidSignupReportFetchOptions";
import type { InvalidSignupReportSearchParams } from "../../generated/dto/InvalidSignupReportSearchParams";
import type { InvalidSignupReportStaff } from "../../generated/dto/InvalidSignupReportStaff";
import type { InvalidSignupReportStaffSearchResult } from "../../generated/dto/InvalidSignupReportStaffSearchResult";
import type { ItemSalesDetailStaff } from "../../generated/dto/ItemSalesDetailStaff";
import type { ItemSalesDetailStaffSearchParams } from "../../generated/dto/ItemSalesDetailStaffSearchParams";
import type { ItemSalesDetailStaffSearchResult } from "../../generated/dto/ItemSalesDetailStaffSearchResult";
import type { ItemSalesReportSearchParams } from "../../generated/dto/ItemSalesReportSearchParams";
import type { ItemSalesReportStaff } from "../../generated/dto/ItemSalesReportStaff";
import type { ItemSalesReportStaffSearchResult } from "../../generated/dto/ItemSalesReportStaffSearchResult";
import type { ItemSalesRequest } from "../../generated/dto/ItemSalesRequest";
import type { ItemSalesStatus } from "../../generated/dto/ItemSalesStatus";
import type { JpClassSignupFetchOptions } from "../../generated/dto/JpClassSignupFetchOptions";
import type { JpClassSignupStaff } from "../../generated/dto/JpClassSignupStaff";
import type { OrderMismatchReportSearchParams } from "../../generated/dto/OrderMismatchReportSearchParams";
import type { OrderMismatchReportStaff } from "../../generated/dto/OrderMismatchReportStaff";
import type { OrderMismatchReportStaffSearchResult } from "../../generated/dto/OrderMismatchReportStaffSearchResult";
import type { OrderMismatchStatus } from "../../generated/dto/OrderMismatchStatus";
import type { PersonFetchOptions } from "../../generated/dto/PersonFetchOptions";
import type { PersonStaff } from "../../generated/dto/PersonStaff";
import type { ReportExportDailySalesRequest } from "../../generated/dto/ReportExportDailySalesRequest";
import type { ReportInvalidOrderRangesRequest } from "../../generated/dto/ReportInvalidOrderRangesRequest";
import type { ReportRunFetchOptions } from "../../generated/dto/ReportRunFetchOptions";
import type { ReportRunRef } from "../../generated/dto/ReportRunRef";
import type { ReportRunStaff } from "../../generated/dto/ReportRunStaff";
import type { ReportScopeStaff } from "../../generated/dto/ReportScopeStaff";
import type { ReportStartInvalidOrdersRequest } from "../../generated/dto/ReportStartInvalidOrdersRequest";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class ReportFrontendService {
  constructor(private readonly api: ReportApi) {}
  async getItemSalesReportStaff(id: number): Promise<ItemSalesReportStaff> {
    return this.api.getItemSalesReportStaff(id)
  }
  async getItemSalesDetailStaff(id: number): Promise<ItemSalesDetailStaff> {
    return this.api.getItemSalesDetailStaff(id)
  }
  async getInvalidOrderReportStaff(id: number, options?: InvalidOrderReportFetchOptions): Promise<InvalidOrderReportStaff> {
    return this.api.getInvalidOrderReportStaff(id, options)
  }
  async getInvalidOrderReasonStaff(id: number): Promise<InvalidOrderReasonStaff> {
    return this.api.getInvalidOrderReasonStaff(id)
  }
  async getDailySalesReportStaff(id: number, options?: DailySalesReportFetchOptions): Promise<DailySalesReportStaff> {
    return this.api.getDailySalesReportStaff(id, options)
  }
  async getDailySalesCategoryAmountStaff(id: number): Promise<DailySalesCategoryAmountStaff> {
    return this.api.getDailySalesCategoryAmountStaff(id)
  }
  async getDailySalesPaymentTypeAmountStaff(id: number): Promise<DailySalesPaymentTypeAmountStaff> {
    return this.api.getDailySalesPaymentTypeAmountStaff(id)
  }
  async getOrderMismatchReportStaff(id: number): Promise<OrderMismatchReportStaff> {
    return this.api.getOrderMismatchReportStaff(id)
  }
  async getInvalidSignupReportStaff(id: number, options?: InvalidSignupReportFetchOptions): Promise<InvalidSignupReportStaff> {
    return this.api.getInvalidSignupReportStaff(id, options)
  }
  async getInvalidSignupReasonStaff(id: number, options?: InvalidSignupReasonFetchOptions): Promise<InvalidSignupReasonStaff> {
    return this.api.getInvalidSignupReasonStaff(id, options)
  }
  async getReportScopeStaff(id: number): Promise<ReportScopeStaff> {
    return this.api.getReportScopeStaff(id)
  }
  async getReportRunStaff(id: number, options?: ReportRunFetchOptions): Promise<ReportRunStaff> {
    return this.api.getReportRunStaff(id, options)
  }
  async searchItemSalesReportStaff(params: ItemSalesReportSearchParams, page: number, perPage: number): Promise<PaginationResult<ItemSalesReportStaffSearchResult>> {
    return this.api.searchItemSalesReportStaff(params, page, perPage)
  }
  async exportItemSalesReportStaffSearchToCsv(params: ItemSalesReportSearchParams, options?: CsvExportOptions): Promise<string> {
    return this.api.exportItemSalesReportStaffSearchToCsv(params, options)
  }
  async searchItemSalesDetailStaff(params: ItemSalesDetailStaffSearchParams, page: number, perPage: number): Promise<PaginationResult<ItemSalesDetailStaffSearchResult>> {
    return this.api.searchItemSalesDetailStaff(params, page, perPage)
  }
  async exportItemSalesDetailStaffSearchToCsv(params: ItemSalesDetailStaffSearchParams, options?: CsvExportOptions): Promise<string> {
    return this.api.exportItemSalesDetailStaffSearchToCsv(params, options)
  }
  async searchInvalidOrderReportStaff(params: InvalidOrderReportSearchParams, page: number, perPage: number): Promise<PaginationResult<InvalidOrderReportStaffSearchResult>> {
    return this.api.searchInvalidOrderReportStaff(params, page, perPage)
  }
  async exportInvalidOrderReportStaffSearchToCsv(params: InvalidOrderReportSearchParams, options?: CsvExportOptions): Promise<string> {
    return this.api.exportInvalidOrderReportStaffSearchToCsv(params, options)
  }
  async searchInvalidOrderReasonStaff(params: InvalidOrderReasonSearchParams, page: number, perPage: number): Promise<PaginationResult<InvalidOrderReasonStaffSearchResult>> {
    return this.api.searchInvalidOrderReasonStaff(params, page, perPage)
  }
  async searchDailySalesReportStaff(params: DailySalesReportSearchParams, page: number, perPage: number): Promise<PaginationResult<DailySalesReportStaffSearchResult>> {
    return this.api.searchDailySalesReportStaff(params, page, perPage)
  }
  async searchDailySalesCategoryAmountStaff(params: DailySalesCategoryAmountSearchParams, page: number, perPage: number): Promise<PaginationResult<DailySalesCategoryAmountStaffSearchResult>> {
    return this.api.searchDailySalesCategoryAmountStaff(params, page, perPage)
  }
  async searchDailySalesPaymentTypeAmountStaff(params: DailySalesPaymentTypeAmountSearchParams, page: number, perPage: number): Promise<PaginationResult<DailySalesPaymentTypeAmountStaffSearchResult>> {
    return this.api.searchDailySalesPaymentTypeAmountStaff(params, page, perPage)
  }
  async searchOrderMismatchReportStaff(params: OrderMismatchReportSearchParams, page: number, perPage: number): Promise<PaginationResult<OrderMismatchReportStaffSearchResult>> {
    return this.api.searchOrderMismatchReportStaff(params, page, perPage)
  }
  async exportOrderMismatchReportStaffSearchToCsv(params: OrderMismatchReportSearchParams, options?: CsvExportOptions): Promise<string> {
    return this.api.exportOrderMismatchReportStaffSearchToCsv(params, options)
  }
  async searchInvalidSignupReportStaff(params: InvalidSignupReportSearchParams, page: number, perPage: number): Promise<PaginationResult<InvalidSignupReportStaffSearchResult>> {
    return this.api.searchInvalidSignupReportStaff(params, page, perPage)
  }
  async exportInvalidSignupReportStaffSearchToCsv(params: InvalidSignupReportSearchParams, options?: CsvExportOptions): Promise<string> {
    return this.api.exportInvalidSignupReportStaffSearchToCsv(params, options)
  }
  async searchInvalidSignupReasonStaff(params: InvalidSignupReasonSearchParams, page: number, perPage: number): Promise<PaginationResult<InvalidSignupReasonStaffSearchResult>> {
    return this.api.searchInvalidSignupReasonStaff(params, page, perPage)
  }
  async searchItemSalesReportStaffFull(params: ItemSalesReportSearchParams, page: number, perPage: number): Promise<PaginationResult<ItemSalesReportStaff>> {
    return this.api.searchItemSalesReportStaffFull(params, page, perPage)
  }
  async searchItemSalesDetailStaffFull(params: ItemSalesDetailStaffSearchParams, page: number, perPage: number): Promise<PaginationResult<ItemSalesDetailStaff>> {
    return this.api.searchItemSalesDetailStaffFull(params, page, perPage)
  }
  async searchInvalidOrderReportStaffFull(params: InvalidOrderReportSearchParams, page: number, perPage: number, options?: InvalidOrderReportFetchOptions): Promise<PaginationResult<InvalidOrderReportStaff>> {
    return this.api.searchInvalidOrderReportStaffFull(params, page, perPage, options)
  }
  async searchInvalidOrderReasonStaffFull(params: InvalidOrderReasonSearchParams, page: number, perPage: number): Promise<PaginationResult<InvalidOrderReasonStaff>> {
    return this.api.searchInvalidOrderReasonStaffFull(params, page, perPage)
  }
  async searchDailySalesReportStaffFull(params: DailySalesReportSearchParams, page: number, perPage: number, options?: DailySalesReportFetchOptions): Promise<PaginationResult<DailySalesReportStaff>> {
    return this.api.searchDailySalesReportStaffFull(params, page, perPage, options)
  }
  async searchDailySalesCategoryAmountStaffFull(params: DailySalesCategoryAmountSearchParams, page: number, perPage: number): Promise<PaginationResult<DailySalesCategoryAmountStaff>> {
    return this.api.searchDailySalesCategoryAmountStaffFull(params, page, perPage)
  }
  async searchDailySalesPaymentTypeAmountStaffFull(params: DailySalesPaymentTypeAmountSearchParams, page: number, perPage: number): Promise<PaginationResult<DailySalesPaymentTypeAmountStaff>> {
    return this.api.searchDailySalesPaymentTypeAmountStaffFull(params, page, perPage)
  }
  async searchOrderMismatchReportStaffFull(params: OrderMismatchReportSearchParams, page: number, perPage: number): Promise<PaginationResult<OrderMismatchReportStaff>> {
    return this.api.searchOrderMismatchReportStaffFull(params, page, perPage)
  }
  async searchInvalidSignupReportStaffFull(params: InvalidSignupReportSearchParams, page: number, perPage: number, options?: InvalidSignupReportFetchOptions): Promise<PaginationResult<InvalidSignupReportStaff>> {
    return this.api.searchInvalidSignupReportStaffFull(params, page, perPage, options)
  }
  async searchInvalidSignupReasonStaffFull(params: InvalidSignupReasonSearchParams, page: number, perPage: number, options?: InvalidSignupReasonFetchOptions): Promise<PaginationResult<InvalidSignupReasonStaff>> {
    return this.api.searchInvalidSignupReasonStaffFull(params, page, perPage, options)
  }
  async getInvalidOrderReportStaffReasons(id: number): Promise<Array<InvalidOrderReasonStaff>> {
    return this.api.getInvalidOrderReportStaffReasons(id)
  }
  async getDailySalesReportStaffCategoryAmounts(id: number): Promise<Array<DailySalesCategoryAmountStaff>> {
    return this.api.getDailySalesReportStaffCategoryAmounts(id)
  }
  async getDailySalesReportStaffPaymentTypeAmounts(id: number): Promise<Array<DailySalesPaymentTypeAmountStaff>> {
    return this.api.getDailySalesReportStaffPaymentTypeAmounts(id)
  }
  async getInvalidSignupReportStaffPersonId(id: number, options?: PersonFetchOptions): Promise<PersonStaff> {
    return this.api.getInvalidSignupReportStaffPersonId(id, options)
  }
  async getInvalidSignupReportStaffApSignupId(id: number, options?: ApClassSignupStaffFetchOptions): Promise<ApClassSignupStaff | null> {
    return this.api.getInvalidSignupReportStaffApSignupId(id, options)
  }
  async getInvalidSignupReportStaffJpSignupId(id: number, options?: JpClassSignupFetchOptions): Promise<JpClassSignupStaff | null> {
    return this.api.getInvalidSignupReportStaffJpSignupId(id, options)
  }
  async getInvalidSignupReportStaffReasons(id: number, options?: InvalidSignupReasonFetchOptions): Promise<Array<InvalidSignupReasonStaff>> {
    return this.api.getInvalidSignupReportStaffReasons(id, options)
  }
  async getInvalidSignupReasonStaffReportId(id: number, options?: InvalidSignupReportFetchOptions): Promise<InvalidSignupReportStaff> {
    return this.api.getInvalidSignupReasonStaffReportId(id, options)
  }
  async getReportRunStaffScopeId(id: number): Promise<ReportScopeStaff> {
    return this.api.getReportRunStaffScopeId(id)
  }
  async dailySalesDatasetStaff(runRef: ReportRunRef): Promise<DailySalesDataset> {
    return this.api.dailySalesDatasetStaff(runRef)
  }
  async dailySalesStatusStaff(request: DailySalesRequest): Promise<DailySalesStatus> {
    return this.api.dailySalesStatusStaff(request)
  }
  async exportDailySalesStaff(request: ReportExportDailySalesRequest): Promise<string> {
    return this.api.exportDailySalesStaff(request)
  }
  async invalidOrderRangesStaff(request: ReportInvalidOrderRangesRequest): Promise<Array<InvalidOrderRangeRun>> {
    return this.api.invalidOrderRangesStaff(request)
  }
  async invalidOrdersStatusStaff(request: InvalidOrderRequest): Promise<InvalidOrderStatus> {
    return this.api.invalidOrdersStatusStaff(request)
  }
  async invalidSignupReconcileStatusStaff(): Promise<InvalidSignupReconcileStatus> {
    return this.api.invalidSignupReconcileStatusStaff()
  }
  async itemSalesStatusStaff(request: ItemSalesRequest): Promise<ItemSalesStatus> {
    return this.api.itemSalesStatusStaff(request)
  }
  async orderMismatchStatusStaff(request: DailySalesRequest): Promise<OrderMismatchStatus> {
    return this.api.orderMismatchStatusStaff(request)
  }
  async startDailySalesStaff(request: DailySalesRequest): Promise<DailySalesStatus> {
    return this.api.startDailySalesStaff(request)
  }
  async startInvalidOrdersStaff(request: ReportStartInvalidOrdersRequest): Promise<InvalidOrderStatus> {
    return this.api.startInvalidOrdersStaff(request)
  }
  async startInvalidSignupReconcileStaff(scope: InvalidSignupReconcileScope): Promise<InvalidSignupReconcileStatus> {
    return this.api.startInvalidSignupReconcileStaff(scope)
  }
  async startItemSalesStaff(request: ItemSalesRequest): Promise<ItemSalesStatus> {
    return this.api.startItemSalesStaff(request)
  }
  async startOrderMismatchStaff(request: DailySalesRequest): Promise<OrderMismatchStatus> {
    return this.api.startOrderMismatchStaff(request)
  }
}
